import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/'), 3000);
      } else {
        alert('Submission failed. Please try again.');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#0A0A0A] border border-[#A2FF00] rounded-sm p-8 text-center">
          <CheckCircle className="w-16 h-16 text-[#A2FF00] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Play, sans-serif' }}>
            Message Sent!
          </h2>
          <p className="text-gray-400" style={{ fontFamily: 'Play, sans-serif' }}>
            Thank you for reaching out. All administrators have been notified and will be in touch soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-[#A2FF00] mb-4 inline-flex items-center gap-2"
            style={{ fontFamily: 'Play, sans-serif' }}
          >
            ← Back to Home
          </button>
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Play, sans-serif' }}>
            Quantum Security & Defence
          </h1>
          <Send className="w-12 h-12 text-[#A2FF00] mx-auto mb-4" />
          <h2 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Play, sans-serif' }}>
            Get Started
          </h2>
          <p className="text-gray-400" style={{ fontFamily: 'Play, sans-serif' }}>
            Fill out the form below and our team will get back to you shortly.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-8 space-y-6" data-testid="contact-form">
          <div>
            <label className="block text-sm font-bold text-gray-400 mb-2" style={{ fontFamily: 'Play, sans-serif' }}>
              Full Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="name-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm text-white p-3"
              placeholder="John Doe"
              style={{ fontFamily: 'Play, sans-serif' }}
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-400 mb-2" style={{ fontFamily: 'Play, sans-serif' }}>
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="email-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm text-white p-3"
              placeholder="john@company.com"
              style={{ fontFamily: 'Play, sans-serif' }}
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-400 mb-2" style={{ fontFamily: 'Play, sans-serif' }}>
              Company *
            </label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              required
              data-testid="company-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm text-white p-3"
              placeholder="Your Company Ltd"
              style={{ fontFamily: 'Play, sans-serif' }}
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-400 mb-2" style={{ fontFamily: 'Play, sans-serif' }}>
              Message *
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              rows={6}
              data-testid="message-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm text-white p-3"
              placeholder="Tell us about your needs..."
              style={{ fontFamily: 'Play, sans-serif' }}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-button"
            className="w-full bg-[#A2FF00] text-black py-4 font-bold uppercase tracking-wider rounded-sm hover:bg-[#8EFF00] transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Play, sans-serif' }}
          >
            {submitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
